import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog} from '@angular/material/dialog';
import {IPatchShiftParams, IPostShiftParams, ShiftService} from '@app/modules/shared/services/shift.service';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {Shift} from '@app/core/models/shift.model';
import {Observable, Subject} from 'rxjs';
import {
	selectIsShiftUpdating,
	selectSelectedDate,
	selectSelectedShift,
	selectUsersWhoBid,
} from '@app/core/store/selectors/schedule.selectors';
import {User} from '@app/core/models/user.model';
import {Group} from '@app/core/models/group.model';
import {IQOSelectData} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-select-data';
import {IQOSelectDataGroup} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-select-data-group';
import {
	AddShiftStockBid,
	AddShiftToStock,
	DeleteShift,
	GetShift,
	PatchShift,
	PostShift,
	RemoveShiftFromStock,
	RemoveShiftStockBid,
	SetSelectedDate
} from '@app/core/store/actions/schedule.actions';
import {Department} from '@app/core/models/department.model';
import {selectDepartment, selectUser} from '@app/core/store/selectors/session.selectors';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {EQORebrandColor, EQORebrandIcon, QOLogService} from '@quickorder/angular-web-lib';
import {UserService} from '@app/modules/shared/services/user.service';
import {Availability} from '@app/core/models/availability.model';
import * as moment from 'moment';
import {Moment} from 'moment';
import {AuthService} from '@app/core/services/auth.service';
import {getCurrentState} from '@app/core/helpers';
import {SnackBar} from '@app/core/services/snackbar.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {GetActivities, GetRelationActivity, SetActivities} from '@app/core/store/actions/activity.actions';
import {Activity, ActivitySubjectTypes} from '@app/core/models/activity.model';
import {
	selectActivities,
	selectIsLoadingActivities,
	selectIsLoadingRelationActivity
} from '@app/core/store/selectors/activity.selectors';
import {
	OpenCreateSickDayModal,
	OpenEmployeeSickDayAnswerModal,
	OpenSickDayAnswersModal
} from '@app/core/store/actions/sick-days.actions';
import {IQODropDownAction} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-drop-down-action.interface';
import {TranslateService} from '@ngx-translate/core';
import {ISickDay} from '@app/core/interfaces/sick-days.interface';
import {selectSickDays} from '@app/core/store/selectors/sick-days.selectors';
import {filter, takeUntil} from 'rxjs/operators';
import {IQOTab} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-tab.interface';

@Component({
	selector: 'app-shift-popup',
	templateUrl: './shift-popup.component.html',
	styleUrls: ['./shift-popup.component.scss'],
})
export class ShiftPopupComponent implements OnInit, OnDestroy {
	public EQORebrandIcon: typeof EQORebrandIcon = EQORebrandIcon;
	public EQORebrandColor: typeof EQORebrandColor = EQORebrandColor;

	public selectedShift$: Observable<Shift> = this.store.select(selectSelectedShift);

	public usersWhoBid$: Observable<User[]> = this.store.select(selectUsersWhoBid);
	public user$: Observable<User> = this.store.select(selectUser);

	public isShiftUpdatingLoading$: Observable<boolean> = this.store.select(selectIsShiftUpdating);
	public activities$: Observable<Activity[]> = this.store.select(selectActivities);
	public isLoadingActivities$: Observable<boolean> = this.store.select(selectIsLoadingActivities);
	public isLoadingRelationActivity$: Observable<boolean> = this.store.select(selectIsLoadingRelationActivity);
	public sickDays$: Observable<ISickDay[]> = this.store.select(selectSickDays);

	public tabs: IQOTab[] = [];
	public currentTab: IQOTab;
	public date: string;
	public momentNow = moment();
	public shiftId: number;
	public workerShiftType: number;
	public userOptions: (IQOSelectData<User> | IQOSelectDataGroup<User>)[] = [];
	public groupOptions: (IQOSelectData<Group> | IQOSelectDataGroup<Group>)[] = [];
	public department: Department;

	public userIdToAvailabilities: { [userId: number]: Availability[] } = {};
	public customMidnight: number;
	public originalSelectedDate: Moment;
	public punchNotApprovedReasons: number[] = [];

	public hasLoadedActivities: boolean = false;

	public shiftActions: IQODropDownAction<{ shiftId: number }>[] = [];
	public sickDay: ISickDay;
	public canCreateSickMessage: boolean;

	public shift: Shift;

	public form: FormGroup;
	private _destroy$: Subject<void> = new Subject<void>();

	constructor(
		public dialog: MatDialog,
		@Inject(MAT_DIALOG_DATA) public data: any,
		public shiftService: ShiftService,
		public store: Store<IAppState>,
		public logService: QOLogService,
		public userService: UserService,
		public authService: AuthService,
		private snackbar: SnackBar,
		private interService: InternationalizationService,
		private translate: TranslateService,
		private readonly formBuilder: FormBuilder,
	) {
		this.createSickDay = this.createSickDay.bind(this);
	}

	public get timeControl(): FormControl {
		return this.form.get('time') as FormControl;
	}

	public get selectedUserControl(): FormControl {
		return this.form.get('selectedUser') as FormControl;
	}

	public get selectedGroupControl(): FormControl {
		return this.form.get('selectedGroup') as FormControl;
	}

	public ngOnInit(): void {
		this.initForm();
		this.initSubscriptions();
		this.customMidnight = parseInt(getCurrentState(this.store).session.tenant.operational_start_time.replace(':', '')); // 04:00 becomes 400
		this.setTabs();
		this.canCreateSickMessage = this.canAddSickMessage();
		this.shiftActions = [{
			rebrandIcon: EQORebrandIcon.edit_dark,
			text: this.translate.instant('SCHEDULE.REGISTER_SICK_DAY'),
			onClick: this.createSickDay,
			clickValue: null,
		}];

	}

	public setWorkerShiftType(): void {
		if (this.selectedUserControl.value.data.id === getCurrentState(this.store).session.user.id) {
			// 1: your shift not on stock - 2: your shift on stock
			(this.shift.public_shift_stock_id === null)
				? this.workerShiftType = 1
				: this.workerShiftType = 2;
		} else {
			if (this.shift.public_shift_stock_id !== null) {
				// 4: another's shift with you bid - 3: another's shift without you bid
				(this.shift.bid_on_shift)
					? this.workerShiftType = 4
					: this.workerShiftType = 3;
			}
		}
	}

	public setTabs(): void {
		this.tabs = [
			{
				index: 0,
				active: true,
				text: this.interService.translate.instant('SCHEDULE.NOTE'),
			},
		];
		if (
			getCurrentState(this.store).session.user.isAdmin() ||
			(this.selectedUserControl.value?.data?.id === getCurrentState(this.store).session.user.id)
		) {
			this.tabs.push({
				index: 1,
				active: false,
				text: this.interService.translate.instant('SCHEDULE.PROBLEMS') + ' (' + this.punchNotApprovedReasons.length + ')',
			});
		}
		if (getCurrentState(this.store).session.user.isAdmin()) {
			this.tabs.push({
				index: 2,
				active: false,
				text: this.interService.translate.instant('SHIFT_HISTORY'),
			});
		}
		this.currentTab = this.tabs[0];
	}

	public addPossibleDay(start: string): void {
		if (!this.originalSelectedDate) {
			return;
		}
		const filledYet = start.indexOf('_') === -1;
		const shouldAddDay = parseInt(start.replace(':', '')) < this.customMidnight;
		let newDate;
		if (filledYet && shouldAddDay) {
			newDate = this.originalSelectedDate.clone().add(1, 'days');
		} else {
			newDate = this.originalSelectedDate.clone();
		}
		if (newDate.format('YYYY-MM-DD') !== this.date) {
			this.store.dispatch(new SetSelectedDate(newDate));
		}
	}

	public setGroupAndUsers(group: Group): void {
		this.selectedGroupControl.patchValue(this.getGroup(group), {emitEvent: false});
		if (!this.selectedGroupControl.value) {
			this.showError('SCHEDULE.GROUP_HAS_BEEN_DELETED');
			// @ts-ignore
			this.selectedGroupControl.patchValue(this.groupOptions[0], {emitEvent: false});
		}
		const groupsUsers = this.getGroupsUsers(this.selectedGroupControl.value.data);
		this.userOptions = this.getUsers(groupsUsers);
		if (this.userOptions.map(option => option['data'].id).indexOf(this.selectedUserControl.value.data.id) < 0) {
			this.selectedUserControl.patchValue(this.extendUser(this.userService.getAvailableUser()), {emitEvent: false});
		}
	}

	public setUserAndGroups(user: User): void {
		this.selectedUserControl.patchValue(this.extendUser(user), {emitEvent: false});
		this.groupOptions = this.getGroups(this.getUsersGroups(user));
		// @ts-ignore
		if (!this.selectedGroupControl.value || !this.groupOptions.find(g => g.data.id === this.selectedGroupControl.value.data.id)) {
			// @ts-ignore

			if (this.groupOptions.length === 0) {
				this.showError('SCHEDULE.EMPLOYEES_IN_NO_GROUPS');
				this.setUserAndGroups(this.userService.getAvailableUser());

				return;
			}
			this.selectedGroupControl.patchValue(this.groupOptions[0], {emitEvent: false});
		}
	}

	public getUsersGroups(user: User): Group[] {
		const groups = getCurrentState(this.store).schedule.groups;

		return groups.filter(group =>
			[this.userService.getAvailableUser()].concat(group.users).find(u => u.id === user.id));
	}

	public getGroupsUsers(group: Group): User[] {
		return [this.userService.getAvailableUser()].concat(group.users);
	}

	public getGroups(groups: Group[]): IQOSelectData<Group>[] {
		return groups.map(g => ({
			text: g.group_name,
			data: g,
		}));
	}

	public getUsers(users: User[]): IQOSelectData<User>[] {
		const usersWhoBid: IQOSelectData<User>[] = [];
		const options = [];
		users.forEach(u => {
			const user = {
				text: u.getFullName(),
				data: u,
			};
			if (this.userIdToAvailabilities[u.id]) {
				let availabilitiesTooltip = this.interService.translate.instant('SCHEDULE.AVAILABILITIES') + ' (' + this.userIdToAvailabilities[u.id].length + '):\n';
				this.userIdToAvailabilities[u.id].forEach((availability, index) => {
					if (availability.reason) {
						availabilitiesTooltip += (index + 1) + '. ' + availability.reason + '\n';
					}
				});
				user['qoIcon'] = {
					icon: EQORebrandIcon.vacation_airplane_dark,
					tooltipPosition: 'above',
					tooltip: availabilitiesTooltip,
				};
			}
			(getCurrentState(this.store).schedule.usersWhoBid.find(uwb => uwb.id === u.id))
				? usersWhoBid.push(user)
				: options.push(user);
		});
		if (usersWhoBid.length > 0) {
			options.unshift(
				{
					options: usersWhoBid,
					text: this.interService.translate.instant('SCHEDULE.EMPLOYEES_WHO_BID'),
				},
			);
		}

		return options;
	}

	public switchTap($event: number): void {
		this.tabs = this.tabs.map(tab => {
			const active = tab.index === $event;
			if (active) {
				this.currentTab = tab;
			}

			return {
				...tab,
				active,
			};
		});
		if (this.currentTab.index === 2 && !this.hasLoadedActivities && this.shiftId && getCurrentState(this.store).session.user.isAdmin()) {
			this.hasLoadedActivities = true;
			this.store.dispatch(new GetActivities(getCurrentState(this.store).session.department.id, ActivitySubjectTypes.shift, this.shiftId));
			this.store.dispatch(new GetRelationActivity(getCurrentState(this.store).session.department.id, ActivitySubjectTypes.shift, this.shiftId, 'shiftStockBids'));
			this.store.dispatch(new GetRelationActivity(getCurrentState(this.store).session.department.id, ActivitySubjectTypes.shift, this.shiftId, 'shiftStock'));
		}
	}

	public save(): void {
		if (this.form.invalid) {
			return;
		}
		const formValue = this.form.value;
		const data: IPatchShiftParams & IPostShiftParams = {
			user_id: formValue.selectedUser.data.id,
			group_id: formValue.selectedGroup.data.id,
			start_date: this.date,
			start_time: this.timeControl.value.start,
			end_time: this.timeControl.value.end,
			pause: formValue.pause,
			note: formValue.note,
			approve_shift: formValue.approved,
		};
		if (!this.shiftId) {
			this.store.dispatch(new PostShift(data));

			return;
		}
		this.store.dispatch(new PatchShift(this.shiftId, data));

	}

	public close(): void {
		this.dialog.getDialogById('shiftPopupDlg')?.close();
	}

	public ngOnDestroy(): void {
		this.store.dispatch(new SetActivities([]));
		this._destroy$.next();
		this._destroy$.complete();
	}

	public delete(): void {
		this.store.dispatch(new DeleteShift(this.shift));
	}

	public addToStock(): void {
		this.store.dispatch(new AddShiftToStock(this.shiftId));

	}

	public removeFromStock(): void {
		this.store.dispatch(new RemoveShiftFromStock(this.shiftId));
	}

	public addBid(): void {
		this.store.dispatch(new AddShiftStockBid(this.shiftId));
	}

	public removeBid(): void {
		this.store.dispatch(new RemoveShiftStockBid(this.shiftId));
	}

	public createSickDay(): void {
		this.store.dispatch(new OpenCreateSickDayModal(this.shiftId, this.date));
	}

	public viewSickDayAnswers(): void {
		if (!this.sickDay || !this.shift) {
			return;
		}
		if (this.authService.currentUser.permission_level === 1) {
			this.store.dispatch(new OpenEmployeeSickDayAnswerModal(this.sickDay));

			return;
		}
		this.store.dispatch(new OpenSickDayAnswersModal(this.sickDay));

	}

	public canAddSickMessage(): boolean {
		if (this.shift) {
			return (moment(this.shift.start.format('YYYY-MM-DD')).isSameOrAfter(moment().subtract(24, 'hours').format('YYYY-MM-DD')) && !this.shift.approved && !this.shift.sick_message_id && this.authService.currentUser?.permission_level === 2);
		}
	}

	private initForm(): void {
		this.form = this.formBuilder.group({
			time: this.formBuilder.group({
				start: ['10:00', Validators.required],
				end: [null, Validators.required],
			}),
			pause: [0, Validators.required],
			selectedUser: [this.extendUser(this.userService.getAvailableUser()), Validators.required],
			selectedGroup: [null, Validators.required],
			note: [null],
			punch_note: [null],
			approved: [false],
		});
	}

	private initSubscriptions(): void {
		this.usersWhoBid$
			.pipe(
				takeUntil(this._destroy$),
				filter(() => !!this.selectedGroupControl.value),
			)
			.subscribe(() => {
				this.setGroupAndUsers(this.selectedGroupControl.value.data);
			});
		this.store.select(selectDepartment)
			.pipe(
				takeUntil(this._destroy$),
			)
			.subscribe(department => this.department = department);

		this.store.select(selectSelectedDate)
			.pipe(
				takeUntil(this._destroy$),
				filter(date => !!date),
			)
			.subscribe(date => {
				this.date = date.format('YYYY-MM-DD');
				if (!this.originalSelectedDate) {
					this.originalSelectedDate = date.clone();
				}
				this.setAvailabilities();
				if (this.selectedGroupControl.value) {
					this.setGroupAndUsers(this.selectedGroupControl.value.data);
				}
			});

		this.selectedShift$
			.pipe(
				takeUntil(this._destroy$),
			)
			.subscribe(shift => {
				const users = getCurrentState(this.store).schedule.users;
				const groups = getCurrentState(this.store).schedule.groups;
				this.userOptions = this.getUsers(users);
				this.groupOptions = this.getGroups(groups);
				if (shift !== null) {

					this.form.patchValue(shift);
					this.timeControl.patchValue({
						start: shift.start.localDate.format('HH:mm'),
						end: shift.end.localDate.format('HH:mm'),
					});
					this.department = shift.department;
					this.shiftId = shift.id;
					this.punchNotApprovedReasons = shift.punch_not_approved_reasons;
					this.shift = shift;

					this.sickDays$
						.pipe(
							takeUntil(this._destroy$),
						)
						.subscribe((sickDays) => {
							if (this.shift?.sick_message_id) {
								this.sickDay = sickDays.find(sick => sick.id === this.shift.sick_message_id);
							} else {
								this.sickDay = null;
							}
						});

					if (shift.amount_of_bids_on_shift > 0) {
						this.store.dispatch(new GetShift(shift.id));
					}
					this.setGroupAndUsers(shift.group);
					// @ts-ignore
					const userStillInGroup = this.getUser(shift.user); // this.userOptions.find(u => u.data.id === shift.user.id);
					if (!userStillInGroup) {
						this.showError('SCHEDULE.EMPLOYEE_NOT_IN_GROUP_ANYMORE');
						this.selectedUserControl.patchValue(this.getUser(this.userService.getAvailableUser()));

						return;
					}
					this.selectedUserControl.patchValue(userStillInGroup);
					this.originalSelectedDate = moment(shift.visible_date);

				} else {
					const selectedModel = getCurrentState(this.store).schedule.selectedModel;
					if (selectedModel instanceof User) {
						this.setUserAndGroups(selectedModel);
					} else {
						this.setGroupAndUsers(selectedModel);
					}
				}
				this.setTabs();
				if (!getCurrentState(this.store).session.user.isAdmin()) {
					this.setWorkerShiftType();
				}

				this.addPossibleDay(this.timeControl.get('start').value);
			});

		this.user$
			.pipe(
				takeUntil(this._destroy$),
				filter(user => !!user),
			)
			.subscribe(user => {
				if (user.isAdmin()) {
					return;
				}

				this.timeControl.disable();
				this.selectedUserControl.disable();
				this.selectedGroupControl.disable();
				this.form.get('approved').disable();
				this.form.get('pause').disable();
			});

		this.selectedUserControl.valueChanges
			.pipe(
				takeUntil(this._destroy$),
			)
			.subscribe(selectedUser => this.setUserAndGroups(selectedUser.data));

		this.selectedGroupControl.valueChanges
			.pipe(
				takeUntil(this._destroy$),
			)
			.subscribe(selectedGroup => this.setGroupAndUsers(selectedGroup.data));
	}

	private setAvailabilities(): void {
		const availabilities = getCurrentState(this.store).schedule.availabilities;
		this.userIdToAvailabilities = {};
		availabilities.forEach(availability => {
			const daysInRange = availability.daysInRange();
			for (let i = 0; i < daysInRange.length; i++) {
				if (daysInRange[i].format('YYYY-MM-DD') === this.date) {
					if (!this.userIdToAvailabilities[availability.user.id]) {
						this.userIdToAvailabilities[availability.user.id] = [];
					}
					this.userIdToAvailabilities[availability.user.id].push(availability);
					break;
				}
			}
		});
	}

	private getUser(selectedUser: User): IQOSelectData<User> {
		let user;
		this.userOptions.forEach(option => {
			// @ts-ignore
			if (option.options) {
				// @ts-ignore
				const foundUser = option.options.find(u => u.data.id === selectedUser.id);
				if (foundUser) {
					user = foundUser;
				}
				// @ts-ignore
			} else if (option.data.id === selectedUser.id) {
				user = option;
			}
		});

		return user;
	}

	private getGroup(group: Group): IQOSelectData<Group> | undefined {
		// @ts-ignore
		return this.groupOptions.find(g => g.data.id === group.id);
	}

	private showError(key: string): void {
		this.snackbar.newError(key);
	}

	private extendUser(user: User): IQOSelectData<User> {
		return {
			data: user,
			text: user.getFullName(),
		};
	}
}
